import { useLazyGetPageByUrlQuery } from '@api/index';
import { EpiPage, LoadingStatus, PageRequest } from '@api/types';
import {
  ContentPageLayout,
  ForbiddenPage,
  LoginPage,
  NotFoundPage,
  ServerErrorPage,
} from '@components';
import { useEditMode } from '@hooks';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export type PageContentLoaderProps = {
  page?: EpiPage;
  contentUrl?: string;
};

/**
 * Page content loader modes:
 * 1. If page is specified, use page data
 * 2. If contentUrl parameter is specified use it to fetch data
 * 3. If nothing specifed, use current location path
 */
export const PageContentLoader = ({ page, contentUrl }: PageContentLoaderProps) => {
  const status: LoadingStatus = {
    isLoading: false,
    isError: false,
    isFetching: false,
    isSuccess: true,
  };
  return page ? (
    <ContentPageLayout page={page} status={status} />
  ) : (
    <PageContentUrlLoader contentUrl={contentUrl} />
  );
};

/**
 * Page Content Loader - load a CMS page using URL.
 * If Content URL is not specified, loads current URL
 */
const PageContentUrlLoader = ({ contentUrl }: PageContentLoaderProps) => {
  // Edit mode data
  const { subscribe } = useEditMode();

  // Page request setup - use contentUrl parameter
  // or try to fetch data using current url
  let location = useLocation();
  const request: PageRequest = {
    contentUrl: contentUrl ? contentUrl : location.pathname,
    expand: '*',
  };
  const [trigger, response] = useLazyGetPageByUrlQuery();
  const { data, isLoading, isSuccess, isError, isFetching, error } = response;
  const page = data?.[0];
  const status = { isLoading, isSuccess, isError, isFetching };
  const statusCode = (data as any)?.status || (error as any)?.status;

  // Fetch fresh page data from EPIi
  const fetchData = () => {
    trigger(request);
  };

  // On component mount - initial data load
  useEffect(() => {
    fetchData();
    subscribe('contentSaved', fetchData);
  }, []);

  useEffect(() => {
    fetchData();
  }, [location]);

  // Page load status
  const isUnauthorizedError = isError && statusCode === 401;
  const isForbiddenError = isError && statusCode === 403;
  const isInternalServerError = isError && statusCode === 500;
  const isPageNotFoundError = isSuccess && !page;
  const isPageLoadSuccess = isSuccess && page;

  return (
    <>
      {isUnauthorizedError && <LoginPage />}
      {isForbiddenError && <ForbiddenPage />}
      {isPageNotFoundError && <NotFoundPage />}
      {isInternalServerError && <ServerErrorPage />}
      {(isLoading || isPageLoadSuccess) && <ContentPageLayout page={page} status={status} />}
    </>
  );
};
